'use client';

import { useRef, useState } from 'react';
import { useLocale } from 'next-intl';
import { getPathname, usePathname } from '@/i18n/navigation';
import { routing } from '@/i18n/routing';
import { getLocaleCode } from '@/helpers/locales';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { cn } from '@/helpers/utils';
import Flag from './Flag';
import SvgChevronDown from './svg/SvgChevronDown';

export default function LanguageSwitcherMobile({ onClick }: { onClick: () => void }) {
    const ref = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(false);
    const locale = useLocale();
    const pathname = usePathname();

    useOnClickOutside(ref, () => setActive(false));

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setActive((prev) => !prev)}
                aria-expanded={active}
                aria-haspopup="true"
                className={cn(
                    'cursor-pointer inline-flex items-center gap-3 pt-[31px] pb-[19px] px-4 mb-4 rounded-bl-[15px] rounded-br-[15px] font-medium transition-colors duration-200',
                    active && 'bg-purple-800 text-white'
                )}
            >
                <Flag country={locale} className="shrink-0" />
                <span className="relative -top-px">{getLocaleCode(locale)}</span>
                <span className="relative top-px">
                    <SvgChevronDown
                        width={10}
                        height={6}
                        className={cn('transform transition-transform duration-200', active && 'rotate-180')}
                    />
                </span>
            </button>

            <div className={cn('absolute top-full right-0 w-full pt-2', active ? 'block' : 'hidden')}>
                <div className="w-full bg-white flex flex-col rounded-[15px] px-4 shadow-submenu">
                    {routing.locales.map((lang) => {
                        if (lang === locale) return null;
                        return (
                            // Full page load on purpose: a soft navigation leaves the old locale's in-flight
                            // prefetches running, and their middleware responses reset NEXT_LOCALE back.
                            <a
                                href={getPathname({ href: pathname, locale: lang, forcePrefix: true })}
                                hrefLang={lang}
                                key={lang}
                                className="relative text-black/60 mouse-hover:text-black flex gap-3 py-4 border-b last:border-0 items-center transition-colors duration-200 submenu-item"
                                onClick={onClick}
                            >
                                <Flag country={lang} className="shrink-0" />
                                <p>{getLocaleCode(lang)}</p>
                            </a>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
